import ArrowForward from '@kmx/mui-icons/KmxArrowRightFilled';
import Icon from '@kmx/mui-icons/KmxDollarBillStroked';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PromoCardProps } from '.';
import { clickTracking } from '../../utils/analytics';
import type { Offer } from '../../utils/offer';
import getVehicleImageData from '../../utils/vehicleImage';

interface OfferCardProps extends PromoCardProps {
    offer: Offer;
}
const OfferCard = ({
    baseUrl = '',
    breakpoint,
    offer,
    originPage = '',
    position = '',
    redirectPath = '/sell-my-car',
}: OfferCardProps) => {
    const { make, model, year } = offer.vehicleInformation.profile;
    const isSmallScreen = useMemo(() => breakpoint == 'xs' || breakpoint == 'sm', [breakpoint]);
    const isExtraLargeScreen = useMemo(() => breakpoint == 'xl', [breakpoint]);

    const [stockImageUrl, setStockImageUrl] = useState<string | null>(null);
    const offerIsExpired = dayjs().isAfter(
        offer.expirationDisplayDate ? offer.expirationDisplayDate : offer.expirationDateUtc,
        'day'
    );

    const handleOnClick = useCallback(() => {
        let providedBaseUrl = baseUrl;
        if (!baseUrl && window?.location?.origin) {
            providedBaseUrl = window.location.origin;
        }

        let url;
        if (offerIsExpired) {
            clickTracking('Refresh my offer', position);
            url = new URL(`${redirectPath}`, providedBaseUrl);
            url.hash = 'offers';
        } else {
            clickTracking('View my offer', position);
            url = new URL(`${redirectPath}/offers/${offer.offerCode}`, providedBaseUrl);
        }

        if (originPage) {
            url.searchParams.append('originPage', originPage);
        }

        if (window) {
            window.open(url, '_self');
        }
    }, [baseUrl, offer.offerCode, offerIsExpired, originPage, position, redirectPath]);

    useEffect(() => {
        if (make && model && year) {
            getVehicleImageData({
                make: make,
                model: model,
                year: year,
                size: 'small',
                angle: 'angled-front',
            }).then(data => setStockImageUrl(data));
        }
    }, [make, model, year]);

    return (
        <Paper
            onClick={handleOnClick}
            sx={{
                borderRadius: isSmallScreen ? '24px' : '32px',
                height: !isSmallScreen && !isExtraLargeScreen ? '204px' : undefined,
                padding: !isSmallScreen && !isExtraLargeScreen ? '12px' : 1,
                paddingRight: isExtraLargeScreen ? '40px' : isSmallScreen ? 1 : '12px',
            }}
            variant="outlined"
        >
            <Grid container spacing={isSmallScreen ? '12px' : '24px'} alignItems={'center'}>
                <Grid size={'auto'}>
                    {stockImageUrl && (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                height: isExtraLargeScreen ? '104px' : isSmallScreen ? '72px' : '180px',
                                justifyContent: 'center',
                                width: isExtraLargeScreen ? '104px' : isSmallScreen ? '72px' : '180px',
                            }}
                        >
                            <img
                                alt="Instant offer"
                                src={stockImageUrl}
                                height={'auto'}
                                width={'100%'}
                                style={{ borderRadius: isSmallScreen ? '16px' : '24px', maxWidth: '100%' }}
                            />
                        </Box>
                    )}
                    {!stockImageUrl && (
                        <Box
                            sx={{
                                alignItems: 'center',
                                bgcolor: 'primary.faint',
                                borderRadius: 3,
                                display: 'flex',
                                height: isExtraLargeScreen ? '104px' : isSmallScreen ? '72px' : '180px',
                                justifyContent: 'center',
                                width: isExtraLargeScreen ? '104px' : isSmallScreen ? '72px' : '180px',
                            }}
                        >
                            <Icon color="primary" sx={{ fontSize: isSmallScreen ? 'large' : '72px' }} />
                        </Box>
                    )}
                </Grid>
                <Grid size={'grow'}>
                    <Stack spacing={1}>
                        {offerIsExpired && (
                            <>
                                <Chip
                                    color="error"
                                    label="Expired"
                                    size="small"
                                    sx={{ width: 'max-content' }}
                                    variant="weak"
                                />
                                <Box>
                                    <Typography component={'h2'} variant={isSmallScreen ? 'subtitle1' : 'h5'}>
                                        {isSmallScreen ? 'Refresh your offer' : "Let's get your current offer"}
                                    </Typography>
                                    <Typography
                                        color="textSecondary"
                                        component={'p'}
                                        variant={isSmallScreen ? 'caption' : 'body2'}
                                    >
                                        {year} {make} {model}
                                    </Typography>
                                </Box>
                                {!isSmallScreen && !isExtraLargeScreen && (
                                    <Button
                                        endIcon={<ArrowForward color="primary" />}
                                        sx={{
                                            width: 'max-content',
                                        }}
                                        variant="outlined"
                                    >
                                        Refresh my offer
                                    </Button>
                                )}
                            </>
                        )}
                        {!offerIsExpired && (
                            <>
                                <Box>
                                    <Typography component={'h2'} variant={isSmallScreen ? 'subtitle1' : 'h6'}>
                                        {year} {make} {model}
                                    </Typography>
                                    <Typography component={'h3'} variant={isSmallScreen ? 'subtitle1' : 'h5'}>
                                        ${Intl.NumberFormat().format(offer.offerAmount)}
                                    </Typography>
                                    <Typography
                                        color="textSecondary"
                                        component={'p'}
                                        variant={isSmallScreen ? 'caption' : 'body2'}
                                    >
                                        Expires{' '}
                                        {dayjs(
                                            offer.expirationDisplayDate
                                                ? offer.expirationDisplayDate
                                                : offer.expirationDateUtc
                                        ).format('dddd, MMMM D')}
                                    </Typography>
                                </Box>

                                {!isSmallScreen && !isExtraLargeScreen && (
                                    <Button
                                        endIcon={<ArrowForward color="primary" />}
                                        sx={{
                                            width: 'max-content',
                                        }}
                                        variant="outlined"
                                    >
                                        View my offer
                                    </Button>
                                )}
                            </>
                        )}
                    </Stack>
                </Grid>
                <Grid
                    container
                    justifyContent={'center'}
                    size={'auto'}
                    sx={{ display: isSmallScreen || isExtraLargeScreen ? 'flex' : 'none' }}
                >
                    {isExtraLargeScreen ? (
                        <Button endIcon={<ArrowForward color="primary" />} variant="outlined">
                            {offerIsExpired ? 'Refresh my offer' : 'View my offer'}
                        </Button>
                    ) : (
                        <ArrowForward color="primary" />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};

export default OfferCard;
